// import matter from 'gray-matter'; // Would need dynamic import setup
import { remark } from 'remark';
import remarkHtml from 'remark-html';
import remarkGfm from 'remark-gfm';

export interface BlogPostData {
  id: string;
  slug: string;
  title: string;
  date: string;
  author: string;
  category: string;
  tags: string[];
  excerpt: string;
  readTime: string;
  published: boolean;
  featuredImage: string;
  content?: string;
  updatedDate?: string;
  series?: string;
  difficulty?: string;
  seoTitle?: string;
  seoDescription?: string;
}

/**
 * Updated blog posts data - manually sync with /blog directory files
 * When you add/update/remove markdown files in /blog, update this array
 */
const STATIC_BLOG_POSTS: BlogPostData[] = [
  {
    id: "2025-08-10-decision-journaling-guide",
    slug: "decision-journaling-guide",
    title: "The Complete Guide to Decision Journaling",
    date: "2025-08-10",
    author: "mathieu@decisionr.xyz",
    category: "Productivity",
    tags: ["journaling", "productivity", "self-improvement", "reflection"],
    excerpt: "Decision journaling is a powerful tool for improving your judgment over time. Learn how to start, what to track, and how to use your journal for continuous improvement.",
    readTime: "12 min read",
    published: true,
    featuredImage: "https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=800&q=80"
  },
  {
    id: "2025-08-01-turing-brain",
    slug: "turing-brain",
    title: "Shared Find: Brains Make Decisions the Way Alan Turing Cracked Codes",
    date: "2025-08-01",
    author: "mathieu@decisionr.com",
    category: "Reshares",
    tags: ["physiology", "decision-framework"],
    excerpt: "A mathematical tool developed during World War II operates in a similar way to brains weighing the reliability of information",
    readTime: "10 min read",
    published: true,
    featuredImage: "https://th-thumbnailer.cdn-si-edu.com/x-Gs3N80S1g57GD6lz-p7Y1j12M=/1026x684/https://tf-cmsv2-smithsonianmag-media.s3.amazonaws.com/filer/ac/d0/acd0ee64-94b9-42cf-85c4-034a6e05f64a/42-17281025.jpg"
  },
  {
    id: "2025-07-31-decisionr-launch",
    slug: "decisionr-launch",
    title: "Decisionr launch",
    date: "2025-07-31",
    author: "mathieu@decisionr.xyz",
    category: "Product",
    tags: ["launch", "product"],
    excerpt: "Today is day one of decisionr. I'd rate the decision to launch as a 10",
    readTime: "2 min read",
    published: true,
    featuredImage: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=800&q=80"
  },
  {
    id: "2025-07-25-psychology-of-decision-making",
    slug: "psychology-of-decision-making",
    title: "The Psychology Behind Better Decision Making",
    date: "2025-01-15",
    author: "mathieu@decisionr.xyz",
    category: "Psychology",
    tags: ["psychology", "cognitive-science", "decision-theory"],
    excerpt: "Understanding the mental processes that drive our choices can dramatically improve our decision-making abilities. Learn about the key psychological principles that influence how we make decisions.",
    readTime: "2 min read",
    published: true,
    featuredImage: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=800&q=80"
  }
];

/**
 * Updated blog content data - manually sync with /blog directory files
 * When you update content in markdown files, update this object
 */
const STATIC_BLOG_CONTENT: Record<string, string> = {
  "2025-08-10-decision-journaling-guide": `# The Complete Guide to Decision Journaling

Decision journaling is one of the most effective ways to improve your judgment over time. By systematically recording your decisions, reasoning, and outcomes, you create a feedback loop that helps you learn from both successes and mistakes.

## What is Decision Journaling?

Decision journaling is the practice of documenting important decisions along with:
- The context and circumstances
- Your reasoning and thought process
- Expected outcomes and confidence levels
- Actual results and lessons learned

It's not about recording every trivial choice, but rather focusing on decisions that matter to your personal or professional life.

## Why Decision Journaling Works

### 1. Combats Hindsight Bias
Our memories are unreliable. We often remember our past reasoning differently than it actually was. A decision journal provides an objective record of your thinking at the time.

### 2. Identifies Patterns
Over time, you'll notice patterns in your decision-making:
- Which types of decisions you handle well
- Common mistakes you tend to make
- Environmental factors that influence your choices

### 3. Improves Self-Awareness
Regular reflection on your decisions builds metacognition—thinking about your thinking. This awareness is crucial for improvement.

### 4. Provides Accountability
Knowing you'll need to document your reasoning encourages more thoughtful decision-making upfront.

## What to Include in Your Decision Journal

### The Decision Entry
For each significant decision, record:

**Basic Information:**
- Date of the decision
- Brief description of the choice
- Key stakeholders involved

**Context:**
- What circumstances led to this decision?
- What constraints or pressures existed?
- What alternatives were considered?

**Reasoning:**
- Why did you choose this option?
- What factors were most important?
- What assumptions did you make?

### The Review Process

Schedule regular reviews (we recommend 7 days, 30 days, and 90 days after major decisions):

**Short-term Review (7 days):**
- Are early indicators positive or negative?
- Any unexpected developments?
- Would you make the same choice again?

**Medium-term Review (30 days):**
- What outcomes have materialized?
- How accurate were your predictions?
- What have you learned so far?

**Long-term Review (90+ days):**
- Final assessment of the decision's impact
- Key lessons learned
- How will this inform future decisions?

## Getting Started: A Step-by-Step Approach

### Week 1: Choose Your Format
Decide whether you'll use:
- A physical notebook
- A digital tool like Notion or Obsidian
- A specialized app like Decisionr.xyz
- A simple text file or document

### Week 2: Start with Small Decisions
Begin with lower-stakes choices to build the habit:
- Which restaurant to try
- What book to read next
- How to organize your workspace

### Week 3: Add Medium-Stakes Decisions
Progress to more significant choices:
- Work project approaches
- Weekend plans
- Small purchases

### Week 4: Include High-Stakes Decisions
Now tackle the big ones:
- Career moves
- Relationship decisions
- Major purchases
- Investment choices

## Common Mistakes to Avoid

### 1. Trying to Journal Every Decision
Focus on decisions that matter. A good rule of thumb: if you spent more than 10 minutes thinking about it, it's worth journaling.

### 2. Being Too Vague
"I chose this because it felt right" isn't helpful. Be specific about your reasoning.

### 3. Skipping the Review Process
The review is where the learning happens. Don't just document—reflect and analyze.

### 4. Judging Past Decisions Too Harshly
Remember that you made decisions with the information available at the time. Focus on learning, not self-criticism.

### 5. Perfectionism
Your journal doesn't need to be perfect. Consistency is more important than perfection.

## Sample Decision Journal Entry

Here's an example of a well-structured decision journal entry:

---

**Date:** January 15, 2025
**Decision:** Accept job offer at startup vs. stay at current company

**Context:** 
Current role feels stagnant, startup offers 30% salary increase but higher risk. Have been at current company for 3 years, comfortable but not growing.

**Alternatives Considered:**
1. Accept startup offer
2. Stay and ask for promotion/raise
3. Look for other opportunities

**Reasoning:**
Choosing startup offer because:
- Significant salary increase addresses financial goals
- Opportunity to learn new skills in growing company
- Current role unlikely to provide growth opportunities
- Worst case: gain experience and network connections

**Predictions:**
- 70% confident this will accelerate career growth
- 60% confident I'll be happier in new role
- 40% chance startup succeeds long-term
- Success = learning new skills, building network, career advancement

**Confidence Level:** 7/10

---

## The Long-Term Benefits

Decision journaling isn't just about improving individual choices—it's about developing better judgment overall. Regular practitioners report:

- Increased confidence in their decision-making
- Better ability to handle uncertainty
- Improved pattern recognition
- Greater self-awareness
- More systematic thinking
- Reduced decision-making anxiety

## Conclusion

Decision journaling is a simple but powerful practice that can transform your judgment over time. Like any skill, it improves with practice and consistency. Start small, be patient with yourself, and focus on the learning process rather than perfect outcomes.

The goal isn't to never make mistakes—it's to learn from them and make better decisions going forward. Your future self will thank you for the clarity and wisdom that comes from this practice.

---

*Ready to start your decision journaling journey? [Try Decisionr.xyz](/) for a structured approach to tracking and analyzing your decisions.*`,

  "2025-08-01-turing-brain": `This post shares an article I came across that I thought was worth highlighting.

# Brains Make Decisions the Way Alan Turing Cracked Codes  
**A mathematical tool developed during World War II operates in a similar way to brains weighing the reliability of information**  

**Devin Powell**, *Smithsonian Magazine*, February 11, 2015

Despite the events depicted in *[The Imitation Game](https://en.wikipedia.org/wiki/The_Imitation_Game)*, Alan Turing did **not** invent the machine that cracked Germany's codes during World War II—Poland did. But the brilliant mathematician did create a mathematical tool for judging the reliability of information—something never mentioned in the film. His tool sped up the decryption of encoded messages using improved versions of the Polish machines.

Now researchers studying rhesus monkeys have found that the brain uses this mathematical tool too—not for decoding messages, but for piecing together unreliable evidence to make simple decisions. For Columbia University neuroscientist [Michael Shadlen](https://www.shadlenlab.columbia.edu) and his team, the finding supports a larger idea that all decisions—even seemingly irrational ones—can be broken down into rational statistical operations. "We think the brain is fundamentally rational," says Shadlen. 

Invented in 1918, the [German Enigma machine](https://www.bletchleypark.org.uk) created a substitution cipher by replacing each original letter with another, producing what seemed like pure gibberish. To complicate things further, the device had rotating disks that swiveled with each keypress, changing the encoding every time.

The system was so complex that even with an Enigma machine on hand, the Germans could only decrypt a message by knowing the initial settings of those encryption dials. Turing devised an algorithm that reduced the number of possible settings the British decryption machines—called *bombes*—had to test each day. Working at the secret Bletchley Park facility in the U.K., Turing realized it was possible to determine whether two messages came from machines with the same rotor settings.

By aligning two encrypted messages vertically, he noticed that the chance of any two letters matching was slightly higher if both messages were from machines with identical initial settings. This was because certain letters occur more frequently in German (as in English), and the encryption process preserved these patterns. 

[Full source](https://www.smithsonianmag.com/science-nature/brains-make-decisions-way-alan-turing-cracked-codes-180954212/?no-ist)`,

  "2025-07-31-decisionr-launch": `# Announcing the Launch of Decisionr.xyz 🎉

I'm excited to introduce **Decisionr.xyz**, a simple and powerful decision journal app designed to help you make better choices every day.

With Decisionr, you can:
- Log your decisions and reflect on outcomes
- Track your thought process and learn from past choices
- Build a habit of mindful decision-making

Whether you're making personal, professional, or team decisions, Decisionr gives you the tools to improve clarity and confidence.

Try it now at [decisionr.xyz](https://decisionr.xyz) and start your journey towards smarter decisions!`,

  "2025-07-25-psychology-of-decision-making": `# The Psychology Behind Better Decision Making

Making good decisions isn't just about having the right information—it's about understanding how our minds work. The field of decision psychology has revealed fascinating insights about why we make the choices we do and how we can improve our decision-making processes.

## The Two Systems of Thinking

Psychologist Daniel Kahneman identified two distinct modes of thinking that influence our decisions:

### System 1: Fast and Automatic
- **Characteristics**: Intuitive, emotional, quick
- **When it's useful**: Routine decisions, familiar situations
- **Potential pitfalls**: Susceptible to biases, can be impulsive

### System 2: Slow and Deliberate  
- **Characteristics**: Analytical, logical, effortful
- **When it's useful**: Complex decisions, unfamiliar situations
- **Potential pitfalls**: Can be overwhelming, prone to analysis paralysis

## Common Decision-Making Biases

Understanding these biases can help you recognize when they might be influencing your choices:

### 1. Confirmation Bias
We tend to seek information that confirms our existing beliefs while ignoring contradictory evidence.

**How to combat it**: Actively seek out opposing viewpoints and challenge your assumptions.

### 2. Anchoring Effect
We rely too heavily on the first piece of information we receive (the "anchor").

**How to combat it**: Generate multiple reference points before making your decision.

### 3. Loss Aversion
We feel the pain of losing something more strongly than the pleasure of gaining something equivalent.

**How to combat it**: Frame decisions in terms of gains rather than losses when possible.

## Practical Strategies for Better Decisions

### The 10-10-10 Rule
Ask yourself: How will I feel about this decision in 10 minutes, 10 months, and 10 years?

### The Pre-mortem Technique
Before making a decision, imagine it has failed. What could have gone wrong? This helps identify potential risks.

### Decision Journaling
Keep track of your decisions and their outcomes. This builds self-awareness and improves future decision-making.

## The Role of Emotions

Contrary to popular belief, emotions aren't the enemy of good decisions—they're essential. Research shows that people with damaged emotional centers in their brains struggle to make even simple decisions.

The key is emotional regulation, not elimination:
- Acknowledge your emotions
- Understand what they're telling you
- Don't let them overwhelm your logical analysis

## Building Decision-Making Skills

Like any skill, decision-making improves with practice:

1. **Start small**: Practice with low-stakes decisions
2. **Reflect regularly**: Review your decisions and their outcomes
3. **Learn from others**: Study how good decision-makers approach choices
4. **Embrace uncertainty**: Accept that perfect information is rarely available

## Conclusion

Understanding the psychology of decision-making gives us powerful tools for improvement. By recognizing our biases, leveraging both intuitive and analytical thinking, and practicing regularly, we can make better choices that align with our goals and values.

Remember: The goal isn't to make perfect decisions, but to make better ones consistently over time.

---

*Want to improve your decision-making skills? Start tracking your decisions with [Decisionr.xyz](/) and discover patterns in your choice-making process.*`
};

/**
 * Process markdown content to HTML
 */
export async function processMarkdown(content: string): Promise<string> {
  try {
    const result = await remark()
      .use(remarkGfm)
      .use(remarkHtml)
      .process(content);
    return result.toString();
  } catch (error) {
    console.error('Error processing markdown:', error);
    return content;
  }
}

/**
 * Get all blog posts
 */
export function getAllBlogPosts(): BlogPostData[] {
  return STATIC_BLOG_POSTS
    .filter(post => post.published)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

/**
 * Get a specific blog post by slug
 */
export function getBlogPost(slug: string): BlogPostData | null {
  return STATIC_BLOG_POSTS.find(post => post.slug === slug) || null;
}

/**
 * Get blog post content
 */
export function getBlogPostContent(id: string): string {
  return STATIC_BLOG_CONTENT[id] || '';
}

/**
 * Get all unique categories from blog posts
 */
export function getBlogCategories(): string[] {
  const categories = STATIC_BLOG_POSTS.map(post => post.category);
  return [...new Set(categories)].sort();
}

/**
 * Get all unique tags from blog posts
 */
export function getBlogTags(): string[] {
  const tags = STATIC_BLOG_POSTS.flatMap(post => post.tags);
  return [...new Set(tags)].sort();
}

/**
 * Search blog posts
 */
export function searchBlogPosts(query: string, category?: string, tag?: string): BlogPostData[] {
  let posts = STATIC_BLOG_POSTS.filter(post => post.published);

  if (query) {
    const searchLower = query.toLowerCase();
    posts = posts.filter(post => 
      post.title.toLowerCase().includes(searchLower) ||
      post.excerpt.toLowerCase().includes(searchLower) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchLower))
    );
  }

  if (category) {
    posts = posts.filter(post => post.category === category);
  }

  if (tag) {
    posts = posts.filter(post => post.tags.includes(tag));
  }

  return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

/**
 * Get related blog posts
 */
export function getRelatedBlogPosts(currentPost: BlogPostData, limit: number = 3): BlogPostData[] {
  if (!currentPost) return [];
  
  return STATIC_BLOG_POSTS
    .filter(post => post.id !== currentPost.id && post.published)
    .filter(post => {
      // Include posts that share tags or category
      const hasSharedTags = post.tags.some(tag => currentPost.tags.includes(tag));
      const hasSharedCategory = post.category === currentPost.category;
      return hasSharedTags || hasSharedCategory;
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, limit);
}